import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { trackGA } from "@/lib/analytics";
import SocialProofToaster from "@/components/SocialProofToaster";
import {
  ArrowRight, ArrowLeft, Check, X, Clock, Bell, Smartphone, Settings as SettingsIcon,
  PaintBucket, MoonStar, ChevronDown, Zap, Lock, Phone, PhoneOff,
  CalendarX, Ghost, Repeat, MapPin, TrendingDown, AlertTriangle,
  Flame, DollarSign, PlayCircle, Mail, MousePointerClick, User, Hammer,
} from "lucide-react";

/**
 * Hook: fetch live "founder spots claimed" count from the backend.
 * Falls back to {loading:true} → defaults so the UI never blocks on this call.
 */
function useFounderSpots() {
  const [data, setData] = useState({ loading: true, taken: null, limit: 100, remaining: null });
  useEffect(() => {
    let alive = true;
    api.get("/public/founder-spots")
      .then((r) => alive && setData({ loading: false, ...r.data }))
      .catch(() => alive && setData({ loading: false, taken: null, limit: 100, remaining: null }));
    return () => { alive = false; };
  }, []);
  return data;
}

/**
 * For-Screen-Companies landing — owner-operator conversion page.
 *
 * Distinct from the homeowner Landing page: this page is built to sell the
 * Instant Quote Tool to screen company owners ($1 trial → $97/mo). Style is
 * deliberately blue-collar / Hormozi-direct, not corporate SaaS.
 *
 * Mounted at both /for-screen-companies (canonical) and /grow-your-screen-company
 * to keep ad-source URLs flexible.
 */
export default function ForScreenCompanies() {
  const [demoOpen, setDemoOpen] = useState(false);

  useEffect(() => {
    document.title = "Stop wasting hours on quotes — WindowScreenQuote for Screen Companies";
    trackGA("scco_page_viewed", {}, { once: true });
  }, []);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (demoOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [demoOpen]);

  const openDemo = () => {
    trackGA("scco_demo_opened");
    setDemoOpen(true);
  };

  return (
    <div className="bg-[#0F172A] text-white selection:bg-[#C75D33] selection:text-white">
      <Nav onSeeHow={openDemo} />
      <Hero onSeeHow={openDemo} />
      <SpeedToLead />
      <HowItWorks />
      <Features />
      <Pain />
      <DemoStrip onSeeHow={openDemo} />
      <Pricing />
      <FAQ />
      <FinalCTA onSeeHow={openDemo} />
      <Footer />
      {demoOpen && <DemoModal onClose={() => setDemoOpen(false)} />}
      <SocialProofToaster />
    </div>
  );
}

/* ─────────────────────────────────── NAV ─────────────────────────────────── */

function Nav({ onSeeHow }) {
  return (
    <header className="sticky top-0 z-40 bg-[#0F172A]/90 backdrop-blur border-b border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="scco-nav-logo">
          <Logo />
        </Link>
        <div className="flex items-center gap-3">
          <button onClick={onSeeHow} className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-white/70 hover:text-white" data-testid="scco-nav-seehow">
            <PlayCircle className="w-4 h-4" /> See how it works
          </button>
          <Link to="/login" className="hidden sm:block text-sm font-semibold text-white/70 hover:text-white" data-testid="scco-nav-login">
            Sign in
          </Link>
          <Link to="/signup" data-testid="scco-nav-signup">
            <Button className="bg-[#C75D33] hover:bg-[#A84C28] rounded-xl h-10 px-5 font-bold">
              Start $1 trial <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

/* ─────────────────────────────────── HERO ────────────────────────────────── */

function Hero({ onSeeHow }) {
  const spots = useFounderSpots();
  const taken = spots.taken;
  const limit = spots.limit || 100;
  const remaining = spots.remaining;

  // Pill copy: prefers live count, falls back to the static spec
  let pillLabel = `For screen company owners only — first ${limit} spots`;
  if (!spots.loading && remaining != null) {
    pillLabel = remaining > 0
      ? `${remaining} founder ${remaining === 1 ? "spot" : "spots"} left of ${limit}`
      : `Founder pricing closed — limit reached`;
  }

  return (
    <section className="relative overflow-hidden border-b border-white/5">
      {/* Background flourish */}
      <div className="pointer-events-none absolute -top-32 -right-40 w-[40rem] h-[40rem] rounded-full bg-[#C75D33]/25 blur-[140px]" />
      <div className="pointer-events-none absolute -bottom-40 -left-32 w-[36rem] h-[36rem] rounded-full bg-yellow-400/10 blur-[140px]" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-24 sm:pt-20 sm:pb-32 relative">
        <div className="inline-flex items-center gap-2 bg-yellow-400 text-black px-3 py-1.5 rounded-full text-[11px] font-extrabold uppercase tracking-widest mb-6" data-testid="scco-hero-pill">
          <Flame className="w-3.5 h-3.5" />
          {pillLabel}
        </div>

        <h1 className="font-display font-extrabold tracking-tight leading-[0.98] text-[44px] sm:text-[64px] lg:text-[80px] max-w-5xl" data-testid="scco-hero-h1">
          Stop wasting hours every week on
          <span className="text-[#C75D33]"> phone calls </span>
          and
          <span className="text-[#C75D33]"> in-person quotes</span>.
        </h1>

        <p className="text-lg sm:text-2xl text-white/80 mt-7 max-w-3xl font-medium leading-snug">
          Let homeowners get <strong className="text-white">instant pricing online</strong> — 24/7, branded to your company, while you're on the truck.
        </p>

        <div className="mt-9 flex flex-col sm:flex-row gap-3 max-w-xl">
          <Link to="/signup" className="flex-1" data-testid="scco-hero-primary-cta">
            <Button className="w-full bg-[#C75D33] hover:bg-[#A84C28] rounded-xl h-14 text-base sm:text-lg font-extrabold tracking-tight shadow-2xl shadow-[#C75D33]/40">
              Start your $1 trial <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          <a href="#" onClick={(e) => { e.preventDefault(); onSeeHow(); }} className="flex-1" data-testid="scco-hero-secondary-cta">
            <Button variant="outline" className="w-full bg-white/5 hover:bg-white/10 border-white/20 hover:border-white/40 text-white rounded-xl h-14 text-base sm:text-lg font-bold">
              <PlayCircle className="w-5 h-5 mr-2" /> See how it works
            </Button>
          </a>
        </div>

        <div className="mt-5 text-sm text-white/60 max-w-xl space-y-0.5">
          <div className="flex items-center gap-2 font-semibold text-white/85">
            <Check className="w-4 h-4 text-emerald-400" /> 30 days for $1, then $97/mo
          </div>
          <div className="flex items-center gap-2 font-semibold text-white/85">
            <Check className="w-4 h-4 text-emerald-400" /> Your $97/mo price never increases
          </div>
          <div className="flex items-center gap-2 font-semibold text-white/85">
            <Check className="w-4 h-4 text-emerald-400" /> Cancel anytime · no contracts
          </div>
        </div>

        {/* Live founder-spot ticker */}
        <FounderSpotsTicker spots={spots} />

        {/* Quick proof bar */}
        <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-4xl">
          <Stat big="60s" small="time to quote (vs hours)" />
          <Stat big="24/7" small="customer pricing" />
          <Stat big="5 min" small="to launch your funnel" />
          <Stat big="$97" small="founder rate · never goes up" />
        </div>
      </div>
    </section>
  );
}

function Stat({ big, small }) {
  return (
    <div className="bg-white/[0.04] border border-white/10 rounded-2xl px-4 py-3">
      <div className="font-display text-3xl sm:text-4xl font-extrabold text-[#FBBF24] leading-none">{big}</div>
      <div className="text-[11px] font-semibold uppercase tracking-widest text-white/55 mt-1.5">{small}</div>
    </div>
  );
}

function FounderSpotsTicker({ spots }) {
  if (spots.loading) {
    return (
      <div className="mt-8 max-w-xl rounded-2xl bg-white/[0.04] border border-white/10 p-4 animate-pulse">
        <div className="h-3 w-32 bg-white/10 rounded" />
        <div className="h-2.5 mt-3 bg-white/10 rounded" />
      </div>
    );
  }
  const taken = Math.max(0, Number(spots.taken ?? 0));
  const limit = Math.max(1, Number(spots.limit || 100));
  const pct = Math.min(100, Math.round((taken / limit) * 100));
  const remaining = Math.max(0, limit - taken);
  const closed = remaining === 0;

  return (
    <div className="mt-8 max-w-xl rounded-2xl bg-white/[0.04] border border-white/10 p-4 sm:p-5" data-testid="scco-founder-ticker">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="relative flex w-2.5 h-2.5">
            <span className={`absolute inset-0 rounded-full ${closed ? "bg-red-400" : "bg-emerald-400"} opacity-75 animate-ping`} />
            <span className={`relative rounded-full w-2.5 h-2.5 ${closed ? "bg-red-500" : "bg-emerald-500"}`} />
          </span>
          <span className="text-[10px] font-extrabold uppercase tracking-widest text-white/70">Live founder count</span>
        </div>
        <div className="text-[10px] font-extrabold uppercase tracking-widest text-white/50">{pct}% full</div>
      </div>
      <div className="font-display text-2xl sm:text-3xl font-extrabold leading-none">
        <span className="text-[#FBBF24]" data-testid="scco-spots-taken">{taken}</span>
        <span className="text-white/40 mx-1.5">of</span>
        <span className="text-white">{limit}</span>
        <span className="text-white/60 text-sm sm:text-base font-bold ml-2.5 align-middle">founder spots taken</span>
      </div>
      <div className="mt-3 h-2 bg-white/10 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-700 ${closed ? "bg-red-500" : "bg-gradient-to-r from-[#C75D33] to-[#FBBF24]"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-2.5 text-xs text-white/65">
        {closed
          ? <>Founder pricing is closed — but you can still <Link to="/signup" className="underline font-bold text-white">join the waitlist</Link>.</>
          : <><strong className="text-white" data-testid="scco-spots-remaining">{remaining}</strong> {remaining === 1 ? "spot" : "spots"} left at the <strong className="text-white">$97/mo</strong> founder rate — yours forever, even when we raise prices later.</>
        }
      </div>
    </div>
  );
}

/* ──────────────────────────── SPEED TO LEAD ──────────────────────────────── */

function SpeedToLead() {
  return (
    <section className="bg-[#0B1220] py-20 sm:py-28 border-y border-white/5">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="text-[#C75D33] font-extrabold uppercase tracking-[0.18em] text-xs mb-3">Speed-to-lead</div>
        <h2 className="font-display text-4xl sm:text-6xl font-extrabold tracking-tight leading-[1.05] max-w-4xl">
          The first company to respond <span className="bg-yellow-400 text-black px-2 -mx-1 inline-block rounded-sm rotate-[-0.6deg]">usually wins</span>.
        </h2>

        <div className="mt-8 max-w-3xl text-lg sm:text-xl text-white/80 leading-relaxed space-y-4">
          <p>Homeowners don't want to wait.</p>
          <p>They want answers <strong>now</strong>.</p>
          <p>When someone searches "screen repair near me" — they're contacting <strong>three or four companies in the same 10-minute window</strong>.</p>
          <p className="text-white/90"><strong>The companies that respond in minutes — not hours — get the job.</strong> Companies that respond within 5 minutes are <em>up to 21x more likely</em> to qualify the lead than the ones replying 30+ minutes later.</p>
          <p>If you're driving to a quote and your phone rings? It already went to the next guy.</p>
        </div>

        {/* Comparison table */}
        <div className="grid md:grid-cols-2 gap-4 mt-12">
          <div className="rounded-3xl bg-red-950/40 border border-red-900/40 p-6 sm:p-8" data-testid="scco-old-way">
            <div className="inline-flex items-center gap-2 bg-red-900/60 text-red-100 px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-widest mb-4">
              <X className="w-3 h-3" /> Old way
            </div>
            <h3 className="font-display text-2xl font-extrabold text-red-100 mb-5">Voicemail tag &amp; truck miles</h3>
            <ul className="space-y-3.5 text-red-50/85">
              <Bad>Phone rings, you're on a ladder, they leave a voicemail</Bad>
              <Bad>You call back 4 hours later — they already booked your competitor</Bad>
              <Bad>Drive 25 minutes to a "quote," walk out with a maybe</Bad>
              <Bad>Slow follow-up by hand · spreadsheets &amp; sticky notes</Bad>
              <Bad>Most quotes never close. You eat the gas &amp; the time.</Bad>
            </ul>
          </div>
          <div className="rounded-3xl bg-emerald-900/20 border border-emerald-500/30 p-6 sm:p-8" data-testid="scco-wsq-way">
            <div className="inline-flex items-center gap-2 bg-emerald-500 text-emerald-950 px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-widest mb-4">
              <Zap className="w-3 h-3" /> WindowScreenQuote
            </div>
            <h3 className="font-display text-2xl font-extrabold text-emerald-50 mb-5">Pricing happens before you pick up the phone</h3>
            <ul className="space-y-3.5 text-emerald-50/90">
              <Good>Homeowner gets <strong>instant pricing</strong> in under 60 seconds</Good>
              <Good>You get a <strong>text + email alert</strong> the moment they finish</Good>
              <Good>"Ready to book" alert when they're serious — you call <em>that</em> one first</Good>
              <Good>Zero truck miles wasted on price-shoppers</Good>
              <Good>Branded to your company — not ours</Good>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function Bad({ children }) {
  return (
    <li className="flex items-start gap-3"><X className="w-4 h-4 text-red-400 mt-0.5 shrink-0" /><span>{children}</span></li>
  );
}
function Good({ children }) {
  return (
    <li className="flex items-start gap-3"><Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /><span>{children}</span></li>
  );
}

/* ────────────────────────────── HOW IT WORKS ─────────────────────────────── */

function HowItWorks() {
  const steps = [
    {
      n: "01",
      title: "Set your pricing",
      body: "Rescreens. New screens. Sliding doors. Retractable. Security doors. Upgrades. You stay in full control — change it anytime.",
    },
    {
      n: "02",
      title: "Brand it in under 5 minutes",
      body: "Drop in your logo, colors, and business info. The whole experience looks like YOUR company — not ours.",
    },
    {
      n: "03",
      title: "Start getting quotes",
      body: "Point Google Ads, Facebook, your Google Business Profile, QR codes, or even direct mail at your new quote URL. Customers get pricing instantly. You get notified instantly.",
    },
  ];
  return (
    <section className="py-20 sm:py-28">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="text-[#C75D33] font-extrabold uppercase tracking-[0.18em] text-xs mb-3">How it works</div>
        <h2 className="font-display text-4xl sm:text-6xl font-extrabold tracking-tight leading-[1.05] max-w-3xl">
          Live in <span className="text-[#C75D33]">5 minutes</span>. No agency. No code.
        </h2>
        <div className="grid md:grid-cols-3 gap-5 mt-12">
          {steps.map((s) => (
            <div key={s.n} className="bg-white/[0.04] border border-white/10 rounded-3xl p-6 sm:p-7 hover:border-[#C75D33]/40 transition-colors" data-testid={`scco-step-${s.n}`}>
              <div className="font-display text-7xl font-extrabold text-[#C75D33]/80 leading-none">{s.n}</div>
              <h3 className="font-display text-2xl font-extrabold mt-4">{s.title}</h3>
              <p className="text-white/70 mt-2 text-base leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────── FEATURES ────────────────────────────────── */

function Features() {
  const items = [
    { icon: Zap, title: "Instant online quotes", body: "Customers get pricing in under 60 seconds. No waiting. No back-and-forth." },
    { icon: SettingsIcon, title: "Your pricing rules", body: "You set every price. Adjust by service, mesh upgrade, or markup whenever you want." },
    { icon: Bell, title: "Text + email alerts", body: "Hot leads ping you the second they finish. Be the first to call back." },
    { icon: Flame, title: "Ready-to-book alerts", body: "Know which customers are serious vs. just price-shopping. Stop chasing the wrong ones." },
    { icon: PaintBucket, title: "Branded experience", body: "Your logo. Your colors. Your business name. Looks built-just-for-you because it is." },
    { icon: MoonStar, title: "24/7 lead capture", body: "Quotes come in while you sleep, eat, or drive between jobs. Nothing slips." },
    { icon: TrendingDown, title: "Less time quoting", body: "Spend hours less per week on phone tag and driveway estimates. More time closing." },
  ];
  return (
    <section className="bg-[#0B1220] py-20 sm:py-28 border-y border-white/5">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="text-[#C75D33] font-extrabold uppercase tracking-[0.18em] text-xs mb-3">Everything in the box</div>
        <h2 className="font-display text-4xl sm:text-6xl font-extrabold tracking-tight leading-[1.05] max-w-4xl">
          Built to <span className="text-[#C75D33]">quote faster</span> and <span className="text-[#C75D33]">close more jobs</span>.
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
          {items.map((it) => (
            <div key={it.title} className="bg-white/[0.04] border border-white/10 rounded-3xl p-6 hover:border-[#C75D33]/40 hover:-translate-y-0.5 transition-all" data-testid={`scco-feature-${it.title.toLowerCase().replace(/[^a-z]/g, "-")}`}>
              <div className="w-11 h-11 rounded-xl bg-[#C75D33]/15 text-[#C75D33] flex items-center justify-center"><it.icon className="w-5 h-5" /></div>
              <h3 className="font-display text-xl font-extrabold mt-4">{it.title}</h3>
              <p className="text-white/65 mt-1.5 leading-relaxed text-[15px]">{it.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────────────── PAIN ──────────────────────────────────── */

function Pain() {
  const pains = [
    { icon: PhoneOff, t: "Missing calls while on jobs" },
    { icon: CalendarX, t: "Driving to quotes that never close" },
    { icon: Ghost, t: "Homeowners ghosting after wasting your time" },
    { icon: TrendingDown, t: "Losing jobs to faster competitors" },
    { icon: Repeat, t: "Answering the same pricing questions on repeat" },
    { icon: Clock, t: "Hours every week burned just quoting" },
  ];
  return (
    <section className="py-20 sm:py-28">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="text-[#C75D33] font-extrabold uppercase tracking-[0.18em] text-xs mb-3">Be honest</div>
        <h2 className="font-display text-4xl sm:text-6xl font-extrabold tracking-tight leading-[1.05] max-w-3xl">
          Still doing quotes <span className="text-[#C75D33]">the old way?</span>
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
          {pains.map((p) => (
            <div key={p.t} className="flex items-start gap-4 rounded-2xl bg-red-950/30 border border-red-900/40 p-5">
              <div className="w-10 h-10 rounded-xl bg-red-900/60 text-red-200 flex items-center justify-center shrink-0">
                <p.icon className="w-4.5 h-4.5" />
              </div>
              <div className="text-base font-bold text-red-50/95 leading-snug pt-1">{p.t}</div>
            </div>
          ))}
        </div>

        <div className="mt-14 rounded-3xl bg-gradient-to-br from-[#C75D33] to-[#A84C28] p-7 sm:p-10 text-white shadow-2xl shadow-[#C75D33]/40">
          <div className="text-yellow-200 font-extrabold uppercase tracking-[0.18em] text-xs mb-2">Now imagine this</div>
          <p className="font-display text-2xl sm:text-4xl font-extrabold leading-tight max-w-3xl">
            What if homeowners already knew your pricing <em className="text-yellow-200 not-italic">before</em> they ever called?
          </p>
          <Link to="/signup" className="inline-block mt-7" data-testid="scco-pain-cta">
            <Button className="bg-white text-[#0F172A] hover:bg-yellow-200 rounded-xl h-13 px-7 text-base font-extrabold tracking-tight">
              Start your $1 trial <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────── DEMO STRIP ─────────────────────────────── */

function DemoStrip({ onSeeHow }) {
  return (
    <section className="bg-[#0B1220] py-20 sm:py-28 border-y border-white/5">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <div className="text-[#C75D33] font-extrabold uppercase tracking-[0.18em] text-xs mb-3">See it in action</div>
          <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">
            A homeowner submits. <span className="text-[#C75D33]">You get this.</span>
          </h2>
          <p className="text-white/75 mt-5 text-lg leading-relaxed">
            One alert tells you everything: scope, ZIP, customer's quote, and whether they're <em>serious</em>. Call the hot ones, skip the tire-kickers, stop wasting truck miles.
          </p>
          <button onClick={onSeeHow} className="inline-block mt-7" data-testid="scco-demo-cta">
            <Button variant="outline" className="bg-white/5 hover:bg-white/10 border-white/20 hover:border-white/40 text-white rounded-xl h-12 px-6 font-bold">
              <PlayCircle className="w-4.5 h-4.5 mr-2" /> See how it works →
            </Button>
          </button>
        </div>

        <div className="lg:col-span-6 relative">
          {/* Mock alert card — designed to feel like an inbox notification */}
          <div className="rounded-3xl bg-white text-slate-900 shadow-2xl shadow-black/40 overflow-hidden border border-white/10" data-testid="scco-demo-alert">
            <div className="bg-[#C75D33] text-white px-6 py-4 flex items-center gap-2.5">
              <Flame className="w-5 h-5" />
              <div className="font-extrabold tracking-tight">🔥 Ready to book</div>
              <div className="ml-auto text-[11px] font-mono opacity-80">just now</div>
            </div>
            <div className="p-6 space-y-3.5">
              <div className="flex items-baseline justify-between gap-3">
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Customer quote</div>
                  <div className="font-display text-4xl font-extrabold text-[#C75D33] mt-1">$420</div>
                </div>
                <div className="text-right">
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Location</div>
                  <div className="font-display text-lg font-bold mt-1 inline-flex items-center gap-1"><MapPin className="w-4 h-4 text-slate-500" /> Murrieta, CA</div>
                </div>
              </div>
              <div className="border-t border-slate-100 pt-3.5 grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Job scope</span><div className="font-bold">6× Window Rescreen</div></div>
                <div><span className="text-slate-500">Customer</span><div className="font-bold">Jen R.</div></div>
                <div><span className="text-slate-500">Phone</span><div className="font-mono font-bold">(951) ••• ••62</div></div>
                <div><span className="text-slate-500">Status</span><div className="inline-flex items-center gap-1 font-bold text-emerald-700"><Flame className="w-3.5 h-3.5" /> Wants to book</div></div>
              </div>
              <div className="pt-2 rounded-xl bg-yellow-50 border border-yellow-200 px-3 py-2 text-xs text-yellow-900">
                <strong>Why this is hot:</strong> They clicked "I'm ready to book" right after seeing the price.
              </div>
            </div>
          </div>
          {/* Decorative stack effect */}
          <div className="absolute -bottom-3 -right-3 -z-0 w-full h-full rounded-3xl bg-white/5 border border-white/10 hidden lg:block" />
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────── PRICING ────────────────────────────────── */

function Pricing() {
  return (
    <section className="py-20 sm:py-28" id="pricing">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
        <div className="inline-flex items-center gap-2 bg-yellow-400 text-black px-3 py-1.5 rounded-full text-[11px] font-extrabold uppercase tracking-widest mb-5">
          <Lock className="w-3.5 h-3.5" /> Early adopter pricing · first 100 only
        </div>
        <h2 className="font-display text-4xl sm:text-6xl font-extrabold tracking-tight leading-[1.05]">
          Your $97/mo price <span className="text-[#C75D33]">never increases</span>.
        </h2>
        <p className="text-white/75 mt-5 text-lg max-w-2xl mx-auto leading-relaxed">
          $97 a month, today. $97 a month, next year. $97 a month when we add SMS routing, custom domains, or whatever else ships next. Cancel anytime — but as long as you stay, your price stays.
        </p>

        {/* Price card */}
        <div className="mt-12 mx-auto max-w-md rounded-3xl bg-white text-slate-900 shadow-2xl shadow-[#C75D33]/30 overflow-hidden border border-white/10" data-testid="scco-price-card">
          <div className="bg-[#C75D33] text-white text-center py-3 font-extrabold uppercase tracking-widest text-xs">Founder rate</div>
          <div className="p-8 sm:p-10 text-center">
            <div className="flex items-baseline justify-center gap-1.5">
              <div className="font-display text-6xl sm:text-7xl font-extrabold text-slate-900">$1</div>
              <div className="text-slate-500 text-base font-semibold">for 30 days</div>
            </div>
            <div className="text-slate-500 mt-2 text-sm">Then <strong className="text-slate-900">$97/month</strong> · cancel anytime · no contracts</div>
            <ul className="text-left mt-7 space-y-2.5">
              {[
                "Full instant-quote funnel, fully branded",
                "Unlimited quotes & customer notifications",
                "Text + email alerts on every quote",
                "Ready-to-book hot-lead flagging",
                "Set your own pricing — change anytime",
                "Your $97/mo price never goes up — even when we raise it for new customers",
              ].map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-slate-700">
                  <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> <span>{b}</span>
                </li>
              ))}
            </ul>
            <Link to="/signup" className="block mt-8" data-testid="scco-pricing-cta">
              <Button className="w-full bg-[#C75D33] hover:bg-[#A84C28] rounded-xl h-14 text-base font-extrabold tracking-tight">
                Start my $1 trial <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <p className="text-[11px] uppercase tracking-widest font-extrabold text-slate-500 mt-4">Limited to first 100 screen companies</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────── FAQ ────────────────────────────────────── */

function FAQ() {
  const items = [
    { q: "Can I set my own pricing?", a: "Yes. Every dollar is yours to set — by service type, mesh upgrade, sliding door, retractable, security, you name it. Change it anytime, customers see the new price immediately." },
    { q: "Can I customize the branding?", a: "Yes. Upload your logo, set your brand colors, drop in your business info. The whole experience reads as YOUR company — not ours." },
    { q: "How fast can I launch?", a: "Usually under 5 minutes. Sign up, enter your pricing, paste in your logo, and you've got a live quote page. Point ads, Google Business Profile, or QR codes at it the same afternoon." },
    { q: "Can customers book instantly?", a: "Yes. Customers can hit 'I'm ready to book' right after seeing their price — you get a hot-lead alert by text and email." },
    { q: "Do I get text notifications?", a: "Yes. Every new quote sends you a text and email instantly so you can be the first to call back. (SMS rolls out for new accounts as TFN approval clears — email is live day one.)" },
    { q: "Can I cancel anytime?", a: "Yes. No contracts. No hidden fees. Cancel in two clicks. If you cancel during the $1 trial, you owe nothing else." },
  ];
  const [open, setOpen] = useState(0);
  return (
    <section className="bg-[#0B1220] py-20 sm:py-28 border-y border-white/5" id="faq">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <div className="text-[#C75D33] font-extrabold uppercase tracking-[0.18em] text-xs mb-3">Common questions</div>
        <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">
          Anything else you need to know.
        </h2>
        <div className="mt-10 space-y-2">
          {items.map((it, idx) => {
            const expanded = open === idx;
            return (
              <button
                key={it.q}
                onClick={() => setOpen(expanded ? -1 : idx)}
                className={`w-full text-left rounded-2xl border transition-colors ${expanded ? "bg-white/[0.06] border-[#C75D33]/40" : "bg-white/[0.03] border-white/10 hover:border-white/20"} p-5`}
                data-testid={`scco-faq-${idx}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-bold text-lg">{it.q}</span>
                  <ChevronDown className={`w-5 h-5 text-white/60 transition-transform ${expanded ? "rotate-180" : ""}`} />
                </div>
                {expanded && (
                  <p className="text-white/75 mt-3 leading-relaxed">{it.a}</p>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────────────── FINAL CTA ─────────────────────────────── */

function FinalCTA({ onSeeHow }) {
  return (
    <section className="relative py-24 sm:py-32 overflow-hidden">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#C75D33]/30 via-[#0F172A] to-[#0F172A]" />
      <div className="pointer-events-none absolute top-1/3 left-1/2 -translate-x-1/2 w-[60rem] h-[60rem] rounded-full bg-[#C75D33]/15 blur-[160px]" />
      <div className="relative max-w-4xl mx-auto px-5 sm:px-8 text-center">
        <div className="inline-flex items-center gap-2 bg-yellow-400 text-black px-3 py-1.5 rounded-full text-[11px] font-extrabold uppercase tracking-widest mb-5">
          <Flame className="w-3.5 h-3.5" /> Limited to first 100 screen companies
        </div>
        <h2 className="font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.02]">
          Quote faster. <span className="text-[#C75D33]">Close more.</span> Drive less.
        </h2>
        <p className="text-white/80 mt-6 text-lg sm:text-xl max-w-2xl mx-auto leading-relaxed">
          One dollar buys you 30 days to see what instant pricing does to your week. Your founder rate stays $97/mo — even when we raise the price for new customers. Cancel anytime, no questions.
        </p>
        <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center max-w-xl mx-auto">
          <Link to="/signup" className="flex-1" data-testid="scco-final-primary-cta">
            <Button className="w-full bg-[#C75D33] hover:bg-[#A84C28] rounded-xl h-14 text-base sm:text-lg font-extrabold tracking-tight shadow-2xl shadow-[#C75D33]/40">
              Start your $1 trial <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          <button onClick={onSeeHow} className="flex-1">
            <Button variant="outline" className="w-full bg-white/5 hover:bg-white/10 border-white/20 hover:border-white/40 text-white rounded-xl h-14 text-base sm:text-lg font-bold">
              <PlayCircle className="w-5 h-5 mr-2" /> See how it works
            </Button>
          </button>
        </div>
        <p className="text-xs text-white/55 mt-5">No contracts · cancel anytime · grandfathered pricing for life</p>
      </div>
    </section>
  );
}

/* ──────────────────────────────── FOOTER ─────────────────────────────────── */

function Footer() {
  return (
    <footer className="bg-[#070C18] border-t border-white/5 py-12">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-5">
        <div className="flex items-center gap-3 text-white/60 text-sm">
          <Logo />
          <span>© {new Date().getFullYear()} WindowScreenQuote</span>
        </div>
        <div className="flex items-center gap-5 text-sm">
          <Link to="/" className="text-white/60 hover:text-white">Homeowners</Link>
          <Link to="/terms" className="text-white/60 hover:text-white">Terms</Link>
          <Link to="/privacy" className="text-white/60 hover:text-white">Privacy</Link>
          <Link to="/contact" className="text-white/60 hover:text-white">Contact</Link>
        </div>
      </div>
    </footer>
  );
}


/* ────────────────────────────── DEMO MODAL ───────────────────────────────── */
/**
 * Annotated walkthrough modal. 6 slides covering the full flow from homeowner
 * arriving on the branded quote page to the company receiving the hot-lead
 * alert and opening the lead in their dashboard. Each slide ships its own
 * CSS mockup (no actual screenshots) so it survives any future UI rebrand
 * without a content team chasing assets.
 */
function DemoModal({ onClose }) {
  const slides = [
    { render: SlideBrandedFunnel, label: "Branded quote page" },
    { render: SlideServicePicker, label: "Service picker" },
    { render: SlideInstantQuote, label: "Instant quote" },
    { render: SlideReadyToBook, label: "Ready to book" },
    { render: SlideOwnerAlert, label: "Owner alert" },
    { render: SlideLeadDashboard, label: "Lead dashboard" },
  ];
  const [idx, setIdx] = useState(0);
  const total = slides.length;
  const current = slides[idx];
  const Render = current.render;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") setIdx((i) => Math.max(0, i - 1));
      else if (e.key === "ArrowRight") setIdx((i) => Math.min(total - 1, i + 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [total, onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      data-testid="scco-demo-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-5xl bg-[#0B1220] border border-white/10 rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]"
      >
        <div className="px-5 sm:px-7 py-4 flex items-center justify-between border-b border-white/10">
          <div className="min-w-0">
            <div className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-[#C75D33]">Step {idx + 1} of {total}</div>
            <div className="font-display text-lg sm:text-xl font-extrabold text-white truncate">{current.label}</div>
          </div>
          <button onClick={onClose} aria-label="Close walkthrough" className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 text-white/70 hover:text-white flex items-center justify-center shrink-0" data-testid="scco-demo-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="relative flex-1 overflow-x-hidden overflow-y-auto px-5 sm:px-10 py-6 sm:py-10 bg-gradient-to-br from-[#0B1220] via-[#0F172A] to-[#0B1220]">
          <Render />
        </div>

        <div className="px-5 sm:px-7 py-4 border-t border-white/10 flex items-center justify-between gap-3 bg-[#070C18]">
          <Button variant="outline" disabled={idx === 0} onClick={() => setIdx((i) => Math.max(0, i - 1))} className="bg-white/5 hover:bg-white/10 border-white/15 text-white rounded-xl h-10 disabled:opacity-30 disabled:cursor-not-allowed" data-testid="scco-demo-prev">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="hidden sm:flex items-center gap-1.5">
            {slides.map((s, i) => (
              <button key={s.label} onClick={() => setIdx(i)} aria-label={`Go to step ${i + 1}: ${s.label}`} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-[#C75D33]" : "w-3 bg-white/20 hover:bg-white/40"}`} data-testid={`scco-demo-dot-${i}`} />
            ))}
          </div>
          {idx < total - 1 ? (
            <div className="flex items-center gap-3 sm:gap-4">
              <Link
                to="/signup"
                onClick={() => { trackGA("scco_demo_skipped", { step: idx + 1 }); onClose(); }}
                className="hidden sm:inline text-xs font-bold uppercase tracking-widest text-white/50 hover:text-white underline-offset-4 hover:underline transition-colors"
                data-testid="scco-demo-skip"
              >
                Skip · start trial
              </Link>
              <Button onClick={() => setIdx((i) => Math.min(total - 1, i + 1))} className="bg-[#C75D33] hover:bg-[#A84C28] rounded-xl h-10 font-bold" data-testid="scco-demo-next">
                Next <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          ) : (
            <Link to="/signup" onClick={onClose} data-testid="scco-demo-final-cta">
              <Button className="bg-[#C75D33] hover:bg-[#A84C28] rounded-xl h-10 font-extrabold">
                Start your $1 trial <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────── Slide layout primitives ─────────────────────────── */

function SlideShell({ title, body, children }) {
  return (
    <div className="grid lg:grid-cols-12 gap-6 sm:gap-10 items-start min-w-0">
      <div className="lg:col-span-5 lg:sticky lg:top-6 min-w-0">
        <div className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight leading-tight break-words">{title}</div>
        <p className="text-white/70 mt-3 text-base leading-relaxed">{body}</p>
      </div>
      <div className="lg:col-span-7 min-w-0 w-full">{children}</div>
    </div>
  );
}

function Annotation({ pin = "", label }) {
  // Hidden on small screens — the floating annotation pins use negative right
  // positions to "stick out" of the fake window, which pushed content past the
  // viewport and caused horizontal scroll on phones. They're purely decorative
  // (the slide title + body already explain each step), so we drop them on
  // mobile and only show them where there's room to do so cleanly.
  //
  // Color: amber/yellow on slate-900 text. Intentionally NOT brand orange so the
  // callouts visually pop AGAINST the orange CTA buttons inside the fake window
  // instead of blending into them.
  return (
    <div className={`hidden sm:block absolute ${pin} bg-[#FBBF24] text-slate-900 text-[10px] sm:text-[11px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full shadow-lg whitespace-nowrap z-10 pointer-events-none`}>
      <span className="absolute inset-0 rounded-full bg-[#FBBF24] opacity-50 animate-pulse" />
      <span className="relative">{label}</span>
    </div>
  );
}

function FakeWindow({ children, brand = "sunshinescreen" }) {
  return (
    <div className="rounded-2xl bg-white text-slate-900 shadow-2xl shadow-black/40 overflow-hidden border border-white/10">
      <div className="bg-slate-100 px-3.5 py-2 flex items-center gap-1.5 border-b border-slate-200">
        <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
        <span className="ml-3 text-[11px] font-mono text-slate-500 truncate">{brand}.windowscreenquote.com/quote</span>
      </div>
      {children}
    </div>
  );
}

function SlideBrandedFunnel() {
  return (
    <SlideShell
      title="Customers land on your branded quote page."
      body="Your logo, your colors, your business name. WindowScreenQuote runs the engine — but your customers never see ours. Embed it in your site, point Google Ads at it, drop a QR on your truck."
    >
      <div className="relative">
        <FakeWindow>
          <div className="px-7 py-9 sm:px-10 sm:py-12 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[#0F172A] text-yellow-300 font-display font-extrabold text-xl mb-4">SC</div>
            <div className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Sunshine Screen Co.</div>
            <div className="text-sm text-slate-500 mt-1.5">Serving Murrieta · Temecula · Wildomar</div>
            <div className="mt-7 text-2xl font-bold text-slate-900 leading-tight">Get an instant quote in 60 seconds</div>
            <div className="text-sm text-slate-500 mt-2 max-w-md mx-auto">Type in your zip, pick your services. We'll text your price right back.</div>
            <button className="mt-6 w-full max-w-xs mx-auto block bg-[#C75D33] text-white font-bold rounded-xl h-12">Start my quote →</button>
          </div>
        </FakeWindow>
        <Annotation pin="top-3 -right-2 sm:right-0" label="Your logo & brand" />
        <Annotation pin="bottom-16 -right-2 sm:right-0" label="One tap · no forms" />
      </div>
    </SlideShell>
  );
}

function SlideServicePicker() {
  return (
    <SlideShell
      title="They pick services in seconds."
      body="No fields to fill. No 'request a callback.' Tap, tap, tap — quantity in, done. Mobile-first because that's where they actually are."
    >
      <div className="relative">
        <FakeWindow>
          <div className="p-7 sm:p-9">
            <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#C75D33] mb-1">Step 2 of 3</div>
            <div className="font-display text-xl font-extrabold text-slate-900">What do you need done?</div>
            <div className="mt-5 space-y-2.5">
              {[
                { label: "Window rescreen", price: "$45 ea", qty: 6, hot: true },
                { label: "New window screen", price: "$85 ea", qty: 0 },
                { label: "Sliding door rescreen", price: "$110 ea", qty: 0 },
                { label: "Retractable screen", price: "$520 ea", qty: 0 },
              ].map((s) => (
                <div key={s.label} className={`flex items-center justify-between rounded-xl border ${s.hot ? "border-[#C75D33] bg-[#FFF6F0]" : "border-slate-200"} px-4 py-3`}>
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-slate-900 truncate">{s.label}</div>
                    <div className="text-xs text-slate-500">{s.price}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="w-7 h-7 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center font-bold">−</span>
                    <span className={`w-7 text-center font-extrabold ${s.qty ? "text-[#C75D33]" : "text-slate-400"}`}>{s.qty}</span>
                    <span className="w-7 h-7 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center font-bold">+</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </FakeWindow>
        <Annotation pin="top-24 right-1 sm:right-3" label="Customer picks quantity" />
        <Annotation pin="bottom-20 right-1 sm:right-3" label="Your pricing — your rules" />
      </div>
    </SlideShell>
  );
}

function SlideInstantQuote() {
  return (
    <SlideShell
      title="Their quote shows up in under a minute."
      body="No waiting. No 'we'll call you back.' They see your price, then choose whether to book or shop around. The serious ones tap the orange button."
    >
      <div className="relative">
        <FakeWindow>
          <div className="p-7 sm:p-9">
            <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#C75D33] mb-1">Your instant quote</div>
            <div className="font-display text-xl font-extrabold text-slate-900">Jen R. · Murrieta, CA 92562</div>
            <div className="mt-5 rounded-2xl border border-slate-200 p-5 space-y-2.5">
              <div className="flex items-center justify-between text-sm"><span className="text-slate-700">6× Window Rescreen</span><span className="font-bold">$270.00</span></div>
              <div className="flex items-center justify-between text-sm"><span className="text-emerald-700">Volume discount (5+)</span><span className="font-bold text-emerald-700">−$30.00</span></div>
              <div className="flex items-center justify-between text-sm"><span className="text-slate-500">Tax</span><span className="font-bold">$0.00</span></div>
              <div className="border-t border-slate-200 pt-3 flex items-baseline justify-between">
                <span className="font-extrabold text-slate-900 text-lg">Your total</span>
                <span className="font-display font-extrabold text-3xl text-[#C75D33]">$240</span>
              </div>
            </div>
            <button className="mt-4 w-full bg-[#C75D33] text-white font-extrabold rounded-xl h-12 inline-flex items-center justify-center">
              I'm ready to book <ArrowRight className="w-4 h-4 ml-1.5" />
            </button>
            <button className="mt-2 w-full bg-white border border-slate-300 text-slate-700 font-bold rounded-xl h-11">Save quote · I'll think about it</button>
          </div>
        </FakeWindow>
        <Annotation pin="top-24 right-1 sm:right-3" label="Itemized · transparent" />
        <Annotation pin="bottom-24 right-1 sm:right-3" label="Hot-lead button" />
      </div>
    </SlideShell>
  );
}

function SlideReadyToBook() {
  return (
    <SlideShell
      title="They click. You hear it ping."
      body="The moment they tap 'I'm ready to book,' we flag the lead as HOT and fire alerts to you. This is the customer worth dropping the ladder for."
    >
      <div className="relative">
        <FakeWindow>
          <div className="p-7 sm:p-9 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-700 mb-4">
              <Check className="w-7 h-7" strokeWidth={3} />
            </div>
            <div className="font-display text-2xl font-extrabold text-slate-900">You're all set, Jen!</div>
            <div className="text-sm text-slate-600 mt-2 max-w-sm mx-auto leading-relaxed">
              Sunshine Screen Co. will text you within one business hour to schedule. We've sent a copy of your $240 quote to <strong>jen.r@gmail.com</strong>.
            </div>
            <div className="mt-6 rounded-2xl bg-yellow-50 border border-yellow-200 px-4 py-3 inline-flex items-center gap-2 text-sm text-yellow-900">
              <Flame className="w-4 h-4" /> <span><strong>Hot-lead alert</strong> sent to your pro</span>
            </div>
          </div>
        </FakeWindow>
        <Annotation pin="top-1/2 -translate-y-1/2 right-1 sm:right-3" label="HOT lead flagged in real time" />
      </div>
    </SlideShell>
  );
}

function SlideOwnerAlert() {
  return (
    <SlideShell
      title="You get the alert in two places, instantly."
      body="Text + email — whichever you grab first. Both land before they finish closing the browser tab. Be the company that calls back first."
    >
      <div className="grid gap-7">
        <div className="relative">
          <div className="rounded-3xl bg-slate-100 p-4 sm:p-5">
            <div className="flex items-center justify-between text-[10px] font-bold text-slate-600 mb-2">
              <span>Messages</span><span>now</span>
            </div>
            <div className="rounded-2xl bg-white p-3.5 shadow-sm border border-slate-200">
              <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">WindowScreenQuote · text alert</div>
              <p className="text-sm text-slate-900 mt-1.5 leading-relaxed">
                🔥 HOT LEAD: Jen R. just clicked 'Ready to Book' on a <strong>$240 quote</strong> · 6× Window Rescreen · Murrieta. Call <strong>(951) 555-0162</strong> NOW.
              </p>
            </div>
          </div>
          <Annotation pin="-top-3 left-4" label="SMS · instant" />
        </div>
        <div className="relative">
          <div className="rounded-2xl bg-white shadow-2xl shadow-black/40 overflow-hidden border border-white/10 text-slate-900">
            <div className="bg-[#C75D33] text-white px-5 py-3.5 flex items-center gap-2 font-extrabold">
              <Flame className="w-5 h-5" /> Hot lead — ready to book
              <span className="ml-auto text-[11px] font-mono opacity-80">just now</span>
            </div>
            <div className="p-5 sm:p-6 space-y-2.5">
              <div className="text-sm text-slate-500">From <strong className="text-slate-900">hello@windowscreenquote.com</strong></div>
              <div className="font-bold text-lg">Jen R. · Murrieta, CA</div>
              <div className="text-sm space-y-1">
                <div><Phone className="w-3.5 h-3.5 inline mr-1 text-slate-500" /> (951) 555-0162</div>
                <div><Mail className="w-3.5 h-3.5 inline mr-1 text-slate-500" /> jen.r@gmail.com</div>
              </div>
              <div className="font-display text-3xl font-extrabold text-[#C75D33]">$240</div>
              <button className="mt-2 inline-flex items-center gap-1.5 text-sm font-extrabold text-white bg-[#0F172A] px-4 h-10 rounded-xl">
                Open lead in dashboard <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          <Annotation pin="-top-3 left-4" label="Email · instant" />
        </div>
      </div>
    </SlideShell>
  );
}

function SlideLeadDashboard() {
  return (
    <SlideShell
      title="One tap → full lead in your dashboard."
      body="Phone, email, exact job scope, the customer's own quote, and a 'Resend quote email' button in case they say they never got it. You're calling them back in under 2 minutes from receiving the alert."
    >
      <div className="relative">
        <div className="rounded-2xl bg-white text-slate-900 shadow-2xl shadow-black/40 overflow-hidden border border-white/10">
          <div className="bg-[#0F172A] text-white px-5 py-3.5 flex items-center gap-2 text-sm">
            <span className="font-bold">Lead detail</span>
            <span className="text-white/40">·</span>
            <span className="font-mono text-[11px] text-white/60">#a1b2c3</span>
            <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest bg-[#C75D33] px-2 py-0.5 rounded-full">
              <Flame className="w-3 h-3" /> Ready to book
            </span>
          </div>
          <div className="p-5 sm:p-7 grid sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2 space-y-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Customer</div>
                <div className="font-display text-2xl font-extrabold">Jen Reynolds</div>
                <div className="text-sm text-slate-500">Murrieta, CA 92562</div>
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                <button className="rounded-xl bg-[#0F172A] text-white text-sm font-bold h-11 inline-flex items-center justify-center gap-1.5">
                  <Phone className="w-4 h-4" /> (951) 555-0162
                </button>
                <button className="rounded-xl bg-slate-100 text-slate-700 text-sm font-bold h-11 inline-flex items-center justify-center gap-1.5">
                  <Mail className="w-4 h-4" /> Email Jen
                </button>
              </div>
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-3.5">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Job scope</div>
                <div className="text-sm font-extrabold mt-1">6× Window Rescreen</div>
                <div className="text-xs text-slate-500 mt-1">Customer's quote: <strong className="text-[#C75D33]">$240</strong></div>
              </div>
            </div>
            <div className="space-y-2.5">
              <div className="rounded-xl bg-yellow-50 border border-yellow-200 p-3.5">
                <div className="text-[10px] font-extrabold uppercase tracking-widest text-yellow-900">Hot signal</div>
                <div className="text-xs text-yellow-900 mt-1 leading-relaxed">Clicked 'Ready to book' 38 seconds after seeing the price.</div>
              </div>
              <button className="w-full rounded-xl bg-[#0F172A] text-white text-sm font-bold h-10 inline-flex items-center justify-center gap-1.5">
                <Mail className="w-3.5 h-3.5" /> Resend quote
              </button>
              <button className="w-full rounded-xl bg-slate-100 text-slate-700 text-xs font-bold h-9">Mark as scheduled</button>
            </div>
          </div>
        </div>
        <Annotation pin="top-44 -left-3" label="Tap to dial" />
        <Annotation pin="top-64 -right-3" label="One-click resend" />
      </div>
    </SlideShell>
  );
}
